import { useState, useEffect, useCallback } from 'react';

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastOnline, setLastOnline] = useState(Date.now());

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnline(Date.now());
      console.log('[Network] Back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[Network] Gone offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connectivity check
    const checkConnectivity = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('/api/health', {
          method: 'HEAD',
          signal: controller.signal,
          cache: 'no-store'
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok && !isOnline) {
          setIsOnline(true);
          setLastOnline(Date.now());
        }
      } catch (error) {
        if (isOnline && !navigator.onLine) {
          setIsOnline(false);
        }
      }
    };

    const intervalId = setInterval(checkConnectivity, 10000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [isOnline]);

  return { isOnline, lastOnline };
}

// Offline data storage utilities
const OFFLINE_STORAGE_KEY = 'fleethub_offline_data';
const OFFLINE_AUTH_KEY = 'fleethub_offline_auth';

export function saveOfflineData(key, data) {
  try {
    const storage = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '{}');
    storage[key] = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error('[Offline] Failed to save data:', error);
  }
}

export function getOfflineData(key, maxAge = 24 * 60 * 60 * 1000) { // 24 hours default
  try {
    const storage = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '{}');
    const item = storage[key];
    
    if (item && (Date.now() - item.timestamp) < maxAge) {
      return item.data;
    }
    return null;
  } catch (error) {
    console.error('[Offline] Failed to get data:', error);
    return null;
  }
}

export function clearOfflineData() {
  localStorage.removeItem(OFFLINE_STORAGE_KEY);
}

// Offline authentication
export function saveOfflineAuth(email, passwordHash, userData) {
  try {
    const authData = {
      email,
      passwordHash,
      userData,
      timestamp: Date.now()
    };
    localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(authData));
  } catch (error) {
    console.error('[Offline] Failed to save auth:', error);
  }
}

export function getOfflineAuth() {
  try {
    const data = localStorage.getItem(OFFLINE_AUTH_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    return null;
  }
}

// Simple hash function for offline password comparison
export async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
